import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import type { AppConfig } from './app-config.model';
import { APP_CONFIG_PATH } from './app-config.constants';

/**
 * Loads S3-managed app configuration once per application session.
 */
@Injectable({ providedIn: 'root' })
export class AppConfigService {
  private readonly http = inject(HttpClient);
  private readonly configState = signal<AppConfig | null>(null);
  private readonly loadRequested = signal(false);

  /** Loaded S3 config, or null when unavailable or not yet fetched. */
  readonly config = this.configState.asReadonly();

  /** Fetches `/app-config/config.json` once; safe to call from every shell component. */
  ensureLoaded(): void {
    if (this.loadRequested()) {
      return;
    }

    this.loadRequested.set(true);
    this.http
      .get<AppConfig>(APP_CONFIG_PATH, {
        headers: new HttpHeaders({ 'Cache-Control': 'no-cache' }),
      })
      .subscribe({
        next: (config) => this.configState.set(config),
        error: (error: unknown) => {
          const status = error instanceof HttpErrorResponse ? error.status : undefined;
          console.error('Failed to retrieve runtime app configuration.', {
            path: APP_CONFIG_PATH,
            status,
          });
          this.configState.set(null);
        },
      });
  }
}
